import React from 'react';

import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { buildDatePresetRange } from '~/services/TimeService';

import {
  COLOR_WHITE,
  CS_FEATURES,
  CS_FEATURE_MAP,
  DEFAULT_CS_FEATURE_IDS,
  MAX_SELECTED_CS_FEATURES,
} from './features/registry';

type Props = {
  selectedFeatureIds: string[];
  selectedTeamLeader: string;
  startAt: string;
  endAt: string;
  teamLeaders: string[];
  updateUrlParams: (updates: Record<string, string>) => void;
};

const MAX_VISIBLE_SELECTED_FEATURE_CHIPS = 2;

export default function CsDashboardFilters({
  selectedFeatureIds,
  selectedTeamLeader,
  startAt,
  endAt,
  teamLeaders,
  updateUrlParams,
}: Props) {
  function applyDatePreset(amount: number, unit: 'day' | 'month') {
    const { startAt, endAt } = buildDatePresetRange(amount, unit);
    updateUrlParams({ start_at: startAt, end_at: endAt });
  }

  const hasNonDefaultFeatures =
    selectedFeatureIds.length !== DEFAULT_CS_FEATURE_IDS.length ||
    selectedFeatureIds.some((id) => !DEFAULT_CS_FEATURE_IDS.includes(id));

  const hasActiveFilters = Boolean(startAt || endAt || selectedTeamLeader || hasNonDefaultFeatures);

  return (
    <Box mt={4}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <FormControl sx={{ width: 420, maxWidth: '100%' }} size="small">
            <InputLabel id="features-label">Features</InputLabel>
            <Select
              labelId="features-label"
              multiple
              value={selectedFeatureIds}
              onChange={(e) => {
                const value = e.target.value;
                const nextSelectedFeatureIds = typeof value === 'string' ? value.split(',') : value;
                if (nextSelectedFeatureIds.length > MAX_SELECTED_CS_FEATURES) return;
                updateUrlParams({ features: nextSelectedFeatureIds.join(',') });
              }}
              input={<OutlinedInput label="Features" />}
              renderValue={(selected) => {
                const selectedIds = selected as string[];
                const visibleFeatureIds = selectedIds.slice(0, MAX_VISIBLE_SELECTED_FEATURE_CHIPS);
                const hiddenFeatureCount = Math.max(0, selectedIds.length - visibleFeatureIds.length);

                return (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ overflow: 'hidden' }}>
                    {visibleFeatureIds.map((id) => {
                      const feature = CS_FEATURE_MAP[id];
                      return feature ? (
                        <Chip
                          key={id}
                          label={feature.label}
                          size="small"
                          sx={{ bgcolor: feature.color, color: COLOR_WHITE, height: 22, maxWidth: 180 }}
                        />
                      ) : null;
                    })}
                    {hiddenFeatureCount > 0 && (
                      <Chip label={`+${hiddenFeatureCount} more`} size="small" sx={{ height: 22 }} />
                    )}
                  </Stack>
                );
              }}
            >
              {CS_FEATURES.map((feature) => (
                <MenuItem
                  key={feature.id}
                  value={feature.id}
                  disabled={
                    selectedFeatureIds.length >= MAX_SELECTED_CS_FEATURES && !selectedFeatureIds.includes(feature.id)
                  }
                >
                  <Checkbox checked={selectedFeatureIds.includes(feature.id)} size="small" />
                  <ListItemText primary={feature.label} />
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {selectedFeatureIds.length}/{MAX_SELECTED_CS_FEATURES} selected
            </FormHelperText>
          </FormControl>

          <FormControl sx={{ minWidth: 220 }} size="small">
            <InputLabel id="team-leader-label">Filter by Team Leader</InputLabel>
            <Select
              labelId="team-leader-label"
              value={selectedTeamLeader}
              label="Filter by Team Leader"
              onChange={(e) => updateUrlParams({ teamLeader: e.target.value })}
            >
              <MenuItem value="">
                <Typography fontStyle="italic">All Agents</Typography>
              </MenuItem>
              {teamLeaders.map((leader) => (
                <MenuItem key={leader} value={leader}>
                  {leader}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Start date"
            type="date"
            size="small"
            value={startAt}
            onChange={(e) => updateUrlParams({ start_at: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="End date"
            type="date"
            size="small"
            value={endAt}
            onChange={(e) => updateUrlParams({ end_at: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />

          <Button variant="outlined" size="small" onClick={() => applyDatePreset(14, 'day')}>
            Last 2 weeks
          </Button>
          <Button variant="outlined" size="small" onClick={() => applyDatePreset(28, 'day')}>
            Last 4 weeks
          </Button>
          <Button variant="outlined" size="small" onClick={() => applyDatePreset(3, 'month')}>
            Last 3 months
          </Button>

          {hasActiveFilters && (
            <Button
              variant="text"
              onClick={() => updateUrlParams({ features: '', start_at: '', end_at: '', teamLeader: '' })}
            >
              Clear filters
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
